import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  useTheme,
  Typography,
  LinearProgress,
  Alert,
  Collapse
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';

import { resetPassword } from '../../../redux/auth/authSlice';
import { API_CALL_STATUS } from '../../../assets/constants/constants';
import Loader from '../../loader/Loader';
import { useNotification } from '../../shared/NotificationProvider';
import FormInput from '../shared/FormInput'; // Use common FormInput

const ResetPasswordForm = () => {
  const theme = useTheme();
  const { token: resetPasswordToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiCallStatus = useSelector(state => state.auth.resetStatus);
  const resetPasswordRes = useSelector(state => state.auth.resetPasswordRes);
  const notify = useNotification();

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, formState: { isSubmitting }, watch, trigger } = methods;
  
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordSuggestions, setPasswordSuggestions] = useState([]);
  const [showPasswordMatch, setShowPasswordMatch] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const newPassword = watch('newPassword', '');
  const confirmPassword = watch('confirmPassword', '');

  // Password strength checker
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(0);
      setPasswordSuggestions([]);
      return;
    }

    let strength = 0;
    const suggestions = [];

    // Check criteria
    if (newPassword.length >= 8) strength += 25;
    else suggestions.push('At least 8 characters');

    if (/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword)) strength += 25;
    else suggestions.push('Mix of uppercase and lowercase letters');

    if (/\d/.test(newPassword)) strength += 25;
    else suggestions.push('Include at least one number');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) strength += 25;
    else suggestions.push('Include at least one special character');

    setPasswordStrength(strength);
    setPasswordSuggestions(suggestions);
  }, [newPassword]);

  // Check password match
  useEffect(() => {
    if (confirmPassword && newPassword) {
      setShowPasswordMatch(true);
      trigger('confirmPassword');
    } else {
      setShowPasswordMatch(false);
    }
  }, [newPassword, confirmPassword, trigger]);

  const getPasswordStrengthColor = () => {
    if (passwordStrength >= 75) return '#00ff88';
    if (passwordStrength >= 50) return '#ffcc00';
    if (passwordStrength >= 25) return '#ff9900';
    return '#ff3366';
  };

  const onSubmit = async (data) => {
    const { newPassword, confirmPassword } = data;

    // Validate token
    if (!resetPasswordToken) {
      notify.error("Invalid reset link. Please request a new password reset.");
      return;
    }

    if (newPassword !== confirmPassword) {
      notify.error("Passwords do not match!");
      return;
    }

    // Check password strength
    if (passwordStrength < 50) {
      notify.warning("Please choose a stronger password for better security.");
      return;
    }

    try {
      const res = await dispatch(resetPassword({
        newPassword,
        confirmPassword,
        resetPasswordToken
      }));

      if (res.payload?.status === 'success') {
        setShowSuccess(true);
        methods.reset();
        notify.success(res.payload.message || "Password reset successfully!");
        
        // Redirect to login after success
        setTimeout(() => {
          navigate("/fmr/sign-in");
        }, 3000);
      } else {
        notify.error(res.payload?.message || "Password reset failed. The link may have expired.");
      }
    } catch (error) {
      notify.error("System error occurred. Please try again.");
    }
  };

  // Validation rules
  const passwordValidation = {
    required: "Password is required",
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters"
    },
    validate: (value) => {
      if (!/[A-Z]/.test(value) || !/[a-z]/.test(value)) {
        return "Include both uppercase and lowercase letters";
      }
      if (!/\d/.test(value)) {
        return "Include at least one number";
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
        return "Include at least one special character";
      }
      return true;
    }
  };

  const confirmPasswordValidation = {
    required: "Please confirm your password",
    validate: (value) => value === newPassword || "Passwords do not match"
  };

  const isPasswordValid = newPassword && !methods.formState.errors.newPassword;
  const isConfirmPasswordValid = confirmPassword && !methods.formState.errors.confirmPassword;

  return (
    <FormProvider {...methods}>
      {apiCallStatus === API_CALL_STATUS.LOADING && <Loader />}

      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          mt: 3,
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          {/* Success Message */}
          <AnimatePresence>
            {showSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -20, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -20, height: 0 }}
              >
                <Alert 
                  severity="success" 
                  icon={<CheckCircleIcon />}
                  sx={{
                    background: 'rgba(0, 255, 136, 0.1)',
                    border: '1px solid rgba(0, 255, 136, 0.3)',
                    color: '#00ff88',
                    borderRadius: '12px',
                    '& .MuiAlert-icon': {
                      color: '#00ff88',
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Password reset successful!
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                    Redirecting to login page in 3 seconds...
                  </Typography>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Security Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Alert 
              icon={<SecurityIcon />}
              sx={{
                background: 'rgba(0, 153, 255, 0.1)',
                border: '1px solid rgba(0, 153, 255, 0.3)',
                color: '#0099ff',
                borderRadius: '12px',
                '& .MuiAlert-icon': {
                  color: '#0099ff',
                },
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                <strong>Create a strong password:</strong> Use a mix of letters, numbers, and special characters for better security.
              </Typography>
            </Alert>
          </motion.div>

          {/* New Password Field */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FormInput
              name="newPassword"
              label="New Password"
              type="password"
              placeholder="Enter strong new password"
              startIcon={<LockIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              showPasswordToggle={true}
              validationRules={passwordValidation}
            />
            
            {/* Password Strength Meter */}
            {newPassword && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                style={{ marginTop: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Password strength:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 600,
                      color: getPasswordStrengthColor(),
                    }}
                  >
                    {passwordStrength >= 75 ? 'Strong 🔒' : 
                     passwordStrength >= 50 ? 'Medium 🛡️' : 
                     passwordStrength >= 25 ? 'Weak ⚠️' : 'Very Weak ❌'}
                  </Typography>
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={passwordStrength} 
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getPasswordStrengthColor(),
                      borderRadius: 3,
                    },
                  }}
                />
                
                {/* Password Suggestions */}
                <Collapse in={passwordSuggestions.length > 0}>
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 0.5 }}>
                      Suggestions to improve:
                    </Typography>
                    <Stack spacing={0.5}>
                      {passwordSuggestions.map((suggestion, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              backgroundColor: '#ff3366',
                            }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            {suggestion}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </motion.div>
            )}
          </motion.div>

          {/* Confirm Password Field */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <FormInput
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              placeholder="Re-enter your new password"
              startIcon={<LockIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              showPasswordToggle={true}
              validationRules={confirmPasswordValidation}
            />
            
            {/* Password Match Indicator */}
            <AnimatePresence>
              {showPasswordMatch && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={{ marginTop: 1 }}
                >
                  {isConfirmPasswordValid ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon sx={{ color: '#00ff88', fontSize: '1rem' }} />
                      <Typography variant="caption" sx={{ color: '#00ff88' }}>
                        Passwords match ✓
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#ff3366' }}>
                      Passwords do not match
                    </Typography>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Password Requirements */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: 2,
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.6)',
                  display: 'block',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                🔐 Password Requirements:
              </Typography>
              <Stack spacing={0.5}>
                {[
                  { text: 'Minimum 8 characters', met: newPassword.length >= 8 },
                  { text: 'Uppercase & lowercase letters', met: /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) },
                  { text: 'At least one number', met: /\d/.test(newPassword) },
                  { text: 'At least one special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) }
                ].map((req, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: req.met ? '#00ff88' : 'rgba(255,255,255,0.2)',
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: req.met ? '#00ff88' : 'rgba(255, 255, 255, 0.6)',
                        textDecoration: req.met ? 'none' : 'line-through',
                        opacity: req.met ? 1 : 0.6,
                      }}
                    >
                      {req.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </motion.div>

          {/* Reset Password Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="gradient"
              type="submit"
              disabled={isSubmitting || apiCallStatus === 'loading' || !isPasswordValid || !isConfirmPasswordValid}
              fullWidth
              startIcon={<LockResetIcon />}
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '12px',
                background: showSuccess 
                  ? 'linear-gradient(45deg, #00cc6d, #0077cc)' 
                  : 'linear-gradient(45deg, #00ff88, #0099ff)',
                color: '#000814',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(0, 255, 136, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isSubmitting || apiCallStatus === 'loading' 
                ? 'Resetting Password...' 
                : showSuccess 
                  ? 'Password Reset!' 
                  : 'Reset Password'}
            </Button>
          </motion.div>

          {/* Additional Security Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Typography 
              variant="caption" 
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textAlign: 'center',
                display: 'block',
                lineHeight: 1.5,
                fontSize: '0.75rem',
              }}
            >
              For security reasons, this link expires in 1 hour. 
              After resetting, you'll be redirected to sign in.
            </Typography>
          </motion.div>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default ResetPasswordForm;